import { UTM, Hemisphere } from '../map/projection';
import { ScalarField, ScalarValueProvider, Point, Bounds, IterationBounds, NativeIterationBounds, ScalarFieldPoint, ScalarFieldValueReducer, ValueRange } from './scalar-field'
import { CubicInterpolator } from '../util/util';
import { Range as TimeRange } from '../timeline/timeline-common';
import { CacheKey } from './scalar-field';
//import { getIsolines } from './marching-squares';
import { getIsolines, LineString } from './optimized-marching-squares';

abstract class BaseScalarField implements ScalarField {

	//reference to a cache system
	private cacheKey: CacheKey;
	// a set of references to other scalar fields that provided values for 
	// this field. When this field is an original only its own key is in
	// the set
	private sources: Set<string>;

	// an array containing for each threshold value a collection (array) of isolines for
	// that value. Each isoline can be made up by 1 or more LineStrings. Each 
	// LineString is either a normal isoline or a boundary line (a helper line in order
	// to close isolines that extend outside the boundaries of the scalar field ).
	private isolines: { value: number, lines: LineString[][] }[];

	public iterator: (topToBottom?: boolean) => Iterable<ScalarFieldPoint>;

	//public  [Symbol.iterator]: Iterable<ScalarFieldPoint>;

	constructor(
		private key: string,
		private utm: UTM,
		private bounds: Bounds,
		private time: TimeRange,
		private stepSize: Point,
		private title: string = 'ScalarField',
		private unit: string = '?',
		public properties?: any) {

		this.cacheKey = undefined;
		this.sources = new Set();

		let sf = this;

		this.iterator = function*(topToBottom?: boolean) {

			let bounds: Bounds = sf.utmBounds();
			let state: ScalarFieldPoint = { index: { x: 0, y: 0 }, position: { x: undefined, y: undefined }, time: sf.time, value: undefined };


			if (topToBottom !== false) {
				for (state.position.y = bounds.y.max; state.position.y >= bounds.y.min; state.position.y -= sf.stepSize.y) {
					for (state.position.x = bounds.x.min; state.position.x <= bounds.x.max; state.position.x += sf.stepSize.x) {
						state.value = sf.value(state.position.x, state.position.y);

						yield { index: { x: state.index.x, y: state.index.y }, position: { x: state.position.x, y: state.position.y }, time: state.time, value: state.value };
						state.index.x++;
					}
					state.index.y++;
					state.index.x = 0;
				}

			} else {
				for (state.position.y = bounds.y.min; state.position.y <= bounds.y.max; state.position.y += sf.stepSize.y) {
					for (state.position.x = bounds.x.min; state.position.x <= bounds.x.max; state.position.x += sf.stepSize.x) {
						state.value = sf.value(state.position.x, state.position.y);

						yield { index: { x: state.index.x, y: state.index.y }, position: { x: state.position.x, y: state.position.y }, time: state.time, value: state.value };
						state.index.x++;
					}
					state.index.y--;
					state.index.x = 0;
				}
			}

		}

	}


	[Symbol.iterator](): Iterable<ScalarFieldPoint> {
		return this.iterator();
	}


	public getKey(): string {
		return this.key;
	}

	public setCacheKey(cacheKey: CacheKey): void {
		this.cacheKey = cacheKey;
		this.sources.clear();
		this.sources.add(cacheKey.key);
	}

	public getCacheKey(): CacheKey {
		return this.cacheKey;
	}

	public addSource(key: string): void {
		this.sources.add(key);
	}

	public hasSource(key: string): boolean {
		return this.sources.has(key);
	}

	public addSources(sources: Set<string>): void {
		sources.forEach(key => this.sources.add(key));
	}

	public getSources(): Set<string> {
		return this.sources;
	}

	public setStepSize(stepSize: Point) {
		this.stepSize = stepSize;
	}

	public getStepSize(): Point {
		return this.stepSize;
	}

	public timeRange(): TimeRange {
		return this.time;
	}

	public projection(): UTM {
		return this.utm;
	}

	public getTitle(): string {
		return this.title;
	}

	public getUnit(): string {
		return this.unit;
	}

	public getProperties(): any {
		return this.properties;
	}

	public utmBounds(): Bounds {
		return this.bounds;
	}
	public getIsolines(): { value: number, lines: LineString[][] }[] {
		return this.isolines;
	}
	public calculateIsoLines(thresholds: number[], sample?: Point): { value: number, lines: LineString[][] }[] {
		if (sample) {
			this.setStepSize(sample);
		}
		this.isolines = getIsolines(this.iterator(), this.iterationBounds(), thresholds);
		return this.isolines;
	}

	public iterationBounds(): IterationBounds {
		let utmBounds = this.utmBounds();
		let size = {
			x: Math.floor((utmBounds.x.max - utmBounds.x.min) / this.stepSize.x) + 1,
			y: Math.floor((utmBounds.y.max - utmBounds.y.min) / this.stepSize.y) + 1
		}
		return {
			x: { min: (utmBounds.x.min), max: (utmBounds.x.min) + (size.x - 1) * this.stepSize.x, step: this.stepSize.x, count: size.x },
			y: { min: (utmBounds.y.max) - (size.y - 1) * this.stepSize.y, max: (utmBounds.y.max), step: -this.stepSize.y, count: size.y }
		};
	}

	public abstract value(x: number, y: number): number;

	public getAsTexture(): { width: number, height: number, values: Float32Array } {
		const bounds = this.iterationBounds();
		return { width: bounds.x.count, height: bounds.y.count, values: Float32Array.from([...this.iterator(false)], (sfp: ScalarFieldPoint) => sfp.value) }
	}
}
export class TimeInterpolatedScalarField extends BaseScalarField {
	private t: number;
	constructor(private sf0: ScalarField, private sf1: ScalarField, millis: number) {

		super(
			undefined,
			sf0.projection(),
			{
				x: {
					min: Math.min(sf0.utmBounds().x.min, sf1.utmBounds().x.min),
					max: Math.max(sf0.utmBounds().x.max, sf1.utmBounds().x.max)
				},
				y: {
					min: Math.min(sf0.utmBounds().y.min, sf1.utmBounds().y.min),
					max: Math.max(sf0.utmBounds().y.max, sf1.utmBounds().y.max)
				}
			},
			{
				start: millis < sf0.timeRange().start ? sf0.timeRange().start : millis > sf1.timeRange().start ? sf1.timeRange().start : millis,
				end: millis < sf0.timeRange().start ? sf0.timeRange().start : millis > sf1.timeRange().start ? sf1.timeRange().start : millis
			},
			sf0.getStepSize()
		);

		this.addSources(sf0.getSources());
		this.addSources(sf1.getSources());

		//TODO maybe produce a value calculator that return undefined when millis is outside intended time range
		this.t = (millis - sf0.timeRange().start) / (sf1.timeRange().start - sf0.timeRange().start);
		this.t = this.t < 0.0 ? 0.0 : this.t > 1.0 ? 1.0 : this.t;
	}

	public value(x: number, y: number): number {
		return (1 - this.t) * this.sf0.value(x, y) + this.t * this.sf1.value(x, y);
	}

}
export class ReducedScalarField extends BaseScalarField {
	constructor(private scalarFields: ScalarField[], private reducer: ScalarFieldValueReducer) {
		super(
			undefined,
			scalarFields[0].projection(),
			scalarFields.reduce((bounds: Bounds, scalarField) => {
				const sfBounds = scalarField.utmBounds();
				bounds.x.min = bounds.x.min ? Math.min(sfBounds.x.min, bounds.x.min) : sfBounds.x.min;
				bounds.x.max = bounds.x.max ? Math.max(sfBounds.x.max, bounds.x.max) : sfBounds.x.max;
				bounds.y.min = bounds.y.min ? Math.min(sfBounds.y.min, bounds.y.min) : sfBounds.y.min;
				bounds.y.max = bounds.y.max ? Math.max(sfBounds.y.max, bounds.y.max) : sfBounds.y.max;
				return bounds;
			}, {
				x: { min: undefined, max: undefined },
				y: { min: undefined, max: undefined }
			}),
			{
				start: scalarFields[0].timeRange().start,
				end: scalarFields[scalarFields.length - 1].timeRange().end,
				width: scalarFields[scalarFields.length - 1].timeRange().end - scalarFields[0].timeRange().start
			},
			scalarFields[0].getStepSize()
		);

		scalarFields.forEach(sf => this.addSources(sf.getSources()));

	}
	value(x: number, y: number): number {
		return this.scalarFields
			.map(scalarSource => scalarSource.value(x, y))
			.reduce(this.reducer, undefined);
	}
}

export class FixedValueScalarField extends BaseScalarField {

	constructor(
		utm: UTM,
		time: TimeRange,
		bounds: Bounds,
		step: Point,
		private scalarValue: number,
		title?: string,
		unit?: string,
		properties?: any) {
		super(undefined, utm, bounds, time, { x: Math.abs(step.x), y: Math.abs(step.y) }, title, unit, properties);
	}

	value(x: number, y: number): number {
		return this.scalarValue;
	}

}

export class CalculatedScalarField extends BaseScalarField {

	constructor(
		key: string,
		utm: UTM,
		time: TimeRange,
		bounds: Bounds,
		step: Point,
		private valueProvider: ScalarValueProvider,
		title?: string,
		unit?: string,
		properties?: any) {
		super(key, utm, bounds, time, { x: Math.abs(step.x), y: Math.abs(step.y) }, title, unit, properties);
	}

	value(x: number, y: number): number {
		return this.valueProvider.value(x, y);
	}

}

/**
 * A scalar field backed by a grid with a certain pitch in memory. The 
 * values between the known grid values are interpolated using bi-cubic
 * interpolation. Therefore the iterator step size can be changed independently
 * from the native grid pitch.
 */
export class BufferedScalarField extends BaseScalarField {

	private dimensions: Point;
	private valueRange: ValueRange;
	
	private point: (cx: number, cy: number) => ScalarFieldSourcePoint;
	private nativeValue: (x: number, y: number) => number;

	public asWorkerArgument(): any {
		return {
			type: 'BufferedScalarField',
			key: this.getKey(),
			utmHemisphere: this.projection().hemisphere(),
			utmZone: this.projection().zone(),
			nativeIterationBounds: this.utmBounds(),
			time: this.timeRange(),
			nativeStepSize: this.nativeStepSize,
			values: this.values,
			outsideBoundsValue: this.outsideBoundsValue,
			title: this.getTitle(),
			unit: this.getUnit(),
			properties: this.getProperties(),
			valuesAlreadyPrepared: true
		}
	}

	constructor(
		key: string,
		utm: UTM,
		private nativeIterationBounds: NativeIterationBounds,
		time: TimeRange,
		private nativeStepSize: Point,
		private values: number[],
		private outsideBoundsValue: number = undefined,
		title?: string,
		unit?: string,
		properties?: any,
		valuesAlreadyPrepared?: boolean) {

		super(key, utm, nativeIterationBounds, time, { x: Math.abs(nativeStepSize.x), y: Math.abs(nativeStepSize.y) }, title, unit, properties);

		this.dimensions = { x: this.nativeIterationBounds.x.count, y: this.nativeIterationBounds.y.count };
		
		this. valueRange = this. values.reduce((range: ValueRange, value) => {
			if (!range) {
				range = {
					min: value,
					max: value
				}
			} else {
				range.min = Math.min(range.min, value);
				range.max = Math.max(range.max, value);
			}
			return range;
		}, undefined);

		if (valuesAlreadyPrepared !== true) {
			this.prepareValues();//add a ring of interpolated values around the originals
		}

		//helper methods for conversion between image-like indices and the underlying data structure
		const iXorg = nativeStepSize.x > 0 ? (ix: number) => ix + 1 : (ix: number) => (this.dimensions.x + 2) - 1 - (ix + 1);
		const iYorg = nativeStepSize.y < 0 ? (iy: number) => iy + 1 : (iy: number) => (this.dimensions.y + 2) - 1 - (iy + 1);
		const iOrg = this.nativeIterationBounds.rowOrientation ? (iXorg: number, iYorg: number) => iXorg + iYorg * (this.dimensions.x + 2) : (iXorg: number, iYorg: number) => iYorg + iXorg * (this.dimensions.y + 2);
		this.nativeValue = (x: number, y: number): number => this.values[iOrg(iXorg(x), iYorg(y))];

		//helper methods for conversion between Cartesian and image-like indices
		const inside = (cx: number, cy: number) => cx === Math.min(Math.max(cx, this.nativeIterationBounds.x.min), this.nativeIterationBounds.x.max) && cy === Math.min(Math.max(cy, this.nativeIterationBounds.y.min), this.nativeIterationBounds.y.max);
		const iXf = (cx: number) => (cx - this.nativeIterationBounds.x.min) / this.nativeIterationBounds.x.step; // Converts x from Cartesian to image-like float index.
		const iYf = (cy: number) => (this.nativeIterationBounds.y.max - cy) / -this.nativeIterationBounds.y.step; // Converts y from Cartesian to image-like float index.

		this.point = (cx: number, cy: number): ScalarFieldSourcePoint => {
			if (inside(cx, cy)) {
				let point: ScalarFieldSourcePoint = { index: { x: undefined, y: undefined, xf: iXf(cx), yf: iYf(cy) }, t: { x: undefined, y: undefined }, position: { x: undefined, y: undefined }, value: undefined };
				point.index.x = Math.floor(point.index.xf);
				point.index.y = Math.floor(point.index.yf);
				point.t.x = point.index.xf - point.index.x; // t is the relative distance from top left corner to actual given point (cx,cy)
				point.t.y = point.index.yf - point.index.y;
				point.value = this.nativeValue(point.index.x, point.index.y);
				point.position.x = this.nativeIterationBounds.x.min + point.index.x * this.nativeIterationBounds.x.step;
				point.position.y = this.nativeIterationBounds.y.max + point.index.y * this.nativeIterationBounds.y.step;
				return point;
			} else {
				return undefined;
			}
		}
	}

	public getAsMessage(): any {
		return {
			key: this.getKey(),
			projecttion: this.projection(),
			nativeIterationBounds: this.getNativeIterationBounds(),
			timeRange: this.timeRange(),
			nativeStapSize: this.getNativeStepSize(),
			outsideBoundsValue: this.getOutsideBoundsValue(),
			title: this.getTitle(),
			unit: this.getUnit(),
			properties: this.getProperties(),
			valuesAlreadyPrepared: true,
			sources: this.getSources()
		};
	}

	public getNativeIterationBounds(): NativeIterationBounds {
		return this.nativeIterationBounds;
	}
	public getNativeStepSize(): Point {
		return this.nativeStepSize;
	}
	public getValues(): number[] {
		return this.values;
	}
	public range(): ValueRange {
		return this.valueRange;
	}
	public getOutsideBoundsValue(): number {
		return this.outsideBoundsValue;
	}

	public value(x: number, y: number): number {
		let p11 = this.point(x, y);

		if (p11) {
			//check boundaries
			let atXEnd = (p11.t.x === 0 && p11.index.x == this.dimensions.x - 1);
			let atYEnd = (p11.t.y === 0 && p11.index.y == this.dimensions.y - 1);
			if (atXEnd || atYEnd) {
				p11.index.x -= (atXEnd ? 1 : 0);
				p11.index.y -= (atYEnd ? 1 : 0);
				p11.value = this.nativeValue(p11.index.x, p11.index.y)
				p11.t.x = atXEnd ? 1.0 : p11.t.x;
				p11.t.y = atYEnd ? 1.0 : p11.t.y;
			}

			let p =
				[
					[
						this.nativeValue(p11.index.x - 1, p11.index.y - 1),
						this.nativeValue(p11.index.x - 1, p11.index.y - 0),
						this.nativeValue(p11.index.x - 1, p11.index.y + 1),
						this.nativeValue(p11.index.x - 1, p11.index.y + 2)
					], [
						this.nativeValue(p11.index.x - 0, p11.index.y - 1),
						p11.value,
						this.nativeValue(p11.index.x - 0, p11.index.y + 1),
						this.nativeValue(p11.index.x - 0, p11.index.y + 2)
					], [
						this.nativeValue(p11.index.x + 1, p11.index.y - 1),
						this.nativeValue(p11.index.x + 1, p11.index.y - 0),
						this.nativeValue(p11.index.x + 1, p11.index.y + 1),
						this.nativeValue(p11.index.x + 1, p11.index.y + 2)
					], [
						this.nativeValue(p11.index.x + 2, p11.index.y - 1),
						this.nativeValue(p11.index.x + 2, p11.index.y - 0),
						this.nativeValue(p11.index.x + 2, p11.index.y + 1),
						this.nativeValue(p11.index.x + 2, p11.index.y + 2)
					]
				];
			return CubicInterpolator.biValue(p, p11.t.x, p11.t.y);
		} else {
			//outside bounds
			return this.outsideBoundsValue;
		}
	};
	/*    
		static initializeIteration( start:Point, dimensions:Point, step:Point): IterationBounds {
			//we need to get our bearings straight. What is up what is down? What is left what is right?
			let internalIteration :IterationBounds ={
					x: { min: undefined, max: undefined, step: undefined, count: dimensions.x },
					y: { min: undefined, max: undefined, step: undefined, count: dimensions.y }
			}
	    
			internalIteration.x.step = Math.abs(step.x);//always positive
			internalIteration.y.step = -1 * Math.abs(step.y);//always negative
			internalIteration.x.min = step.x > 0 ? start.x : start.x + (dimensions.x-1) * (step.x);
			internalIteration.y.max = step.y < 0 ? start.y : start.y + (dimensions.y-1) * (step.y);
			internalIteration.x.max = internalIteration.x.min + (dimensions.x - 1) * internalIteration.x.step;
			internalIteration.y.min = internalIteration.y.max + (dimensions.y - 1) * internalIteration.y.step;
			return internalIteration;
		}
	  */
	//add a ring of interpolated values around the originals
	private prepareValues() {
		const nativeIteration = <IterationBounds>this.utmBounds();

		const dimensions: Point = { x: nativeIteration.x.count, y: nativeIteration.y.count };
		//define this helper function
		const value = (x: number, y: number) => this.nativeIterationBounds.rowOrientation ? this.values[x + y * this.dimensions.x] : this.values[y + x * this.dimensions.y];
		const virtualValue = (x: number, y: number) => this.nativeIterationBounds.rowOrientation ? this.values[x + y * (this.dimensions.x + 2)] : this.values[y + x * (this.dimensions.y + 2)];
		const virtualP = (p0: number, p1: number) => 2 * p0 - p1;

		let i, p0, p1;
		//first calculate the new virtual corner points which are a little special case
		const p_00 = virtualP(value(0, 0), value(1, 1));
		const p_n0 = virtualP(value(this.dimensions.x - 1, 0), value(this.dimensions.x - 2, 1));
		const p_0n = virtualP(value(0, this.dimensions.y - 1), value(1, this.dimensions.y - 2));
		const p_nn = virtualP(value(this.dimensions.x - 1, this.dimensions.y - 1), value(this.dimensions.x - 2, this.dimensions.y - 2));

		if (this.nativeIterationBounds.rowOrientation) {

			//give each row a new start and end
			for (i = this.values.length; i > 0;) {
				//add new (virtual) end point
				p0 = this.values[i - 2];
				p1 = this.values[i - 1];
				this.values.splice(i, 0, virtualP(p1, p0));

				//add new (virtual) start point
				i -= this.dimensions.x
				p0 = this.values[i];
				p1 = this.values[i + 1];
				this.values.splice(i, 0, virtualP(p0, p1));
			}

			//create new first row
			const first = new Array(this.dimensions.x + 2).fill(0).map((slot, i) => virtualP(virtualValue(i, 0), virtualValue(i, 1)));
			//set the special cases
			first.splice(0, 1, p_00);
			first.splice(first.length - 1, 1, p_n0);

			//create new last row
			const last = new Array(this.dimensions.x + 2).fill(0).map((slot, i) => virtualP(virtualValue(i, this.dimensions.y - 1), virtualValue(i, this.dimensions.y - 2)));
			//set the special cases
			last.splice(0, 1, p_0n);
			last.splice(last.length - 1, 1, p_nn);

			//add first and last to values
			this.values.splice(0, 0, ...first);
			this.values.splice(this.values.length, 0, ...last);
		} else {

			//give each column a new start and end
			for (i = this.values.length; i > 0;) {
				//add new (virtual) end point
				p0 = this.values[i - 2];
				p1 = this.values[i - 1];
				this.values.splice(i, 0, virtualP(p1, p0));

				//add new (virtual) start point
				i -= this.dimensions.y
				p0 = this.values[i];
				p1 = this.values[i + 1];
				this.values.splice(i, 0, virtualP(p0, p1));
			}

			//create new first column
			const first = new Array(this.dimensions.y + 2).fill(0).map((slot, i) => virtualP(virtualValue(0, i), virtualValue(1, i)));
			//set the special cases
			first.splice(0, 1, p_00);
			first.splice(first.length - 1, 1, p_0n);

			//create new last column
			const last = new Array(this.dimensions.y + 2).fill(0).map((slot, i) => virtualP(virtualValue(this.dimensions.x - 1, i), virtualValue(this.dimensions.x - 2, i)));
			//set the special cases
			last.splice(0, 1, p_n0);
			last.splice(last.length - 1, 1, p_nn);

			//add first and last to values
			this.values.splice(0, 0, ...first);
			this.values.splice(this.values.length, 0, ...last);
		}
	}
}

export class BufferedValueMappedScalarField extends BufferedScalarField {

	constructor(sourceScalarField: BufferedScalarField, sources: Set<string>[], valueMapper: (value: number, index: number, values: number[]) => number) {
		super(
			sourceScalarField.getKey(),
			sourceScalarField.projection(),
			sourceScalarField.getNativeIterationBounds(),
			sourceScalarField.timeRange(),
			sourceScalarField.getNativeStepSize(),
			sourceScalarField.getValues().map(valueMapper),
			sourceScalarField.getOutsideBoundsValue(),
			sourceScalarField.getTitle(),
			sourceScalarField.getUnit(),
			sourceScalarField.getProperties(),
			true);

		this.addSources(sourceScalarField.getSources());
		sources.forEach(sfSources => this.addSources(sfSources));
	}
}

/**
 * interface used by the ScalarFieldInstantSource class only
 */
interface ScalarFieldSourcePoint {
	index: {
		x: number, //Math.floor(xf) 
		y: number, //Math.floor(xy)
		xf: number, //floating point x index
		yf: number //floating point y index
	},
	t: Point, //the fraction part of the index
	position: Point, //the actual external position (e.g. UTM coordinate without zone and hemisphere)
	value: number //the actual scalar value for this position
}
